import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { isAnswerableQuestionType, type TSubmitFormAnswer } from '@onesource/schemas';
import type { TFormQuestion } from '@onesource/services';
import { sanitizeRichHtml } from '@onesource/utils';

import { QuestionRenderer } from './question-renderer';
import { FORM_CONTENT_MAX_WIDTH, formRichContentSx } from './rich-content-sx';

interface QuestionBlockProps {
  question: TFormQuestion;
  answer: TSubmitFormAnswer;
  onAnswerChange: (answer: TSubmitFormAnswer) => void;
  disabled?: boolean;
  /**
   * Left-aligned with tighter spacing, for a question inside a side panel (the
   * teacher's feedback) rather than a page of its own.
   */
  compact?: boolean;
}

/**
 * Renders a single question — required indicator, question text/body/explanation,
 * and the type-specific answer UI. Used for both standalone-question pages and
 * each question inside a group page.
 */
export const QuestionBlock = ({
  question,
  answer,
  onAnswerChange,
  disabled = false,
  compact = false,
}: QuestionBlockProps) => {
  const align = compact ? 'left' : 'center';
  const gap = compact ? 1.25 : { xs: 2, md: 4 };
  // A content-only question (a workbook text block) owns its whole area: the
  // design shows just its headline/body blocks, with none of the centred question
  // chrome above them. `questionText` is still required by the schema and is what
  // identifies the block in the builder — it is simply not member-facing here.
  if (!isAnswerableQuestionType(question.questionType)) {
    return (
      <Box sx={{ mb: 5, maxWidth: FORM_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <QuestionRenderer question={question} answer={answer} onAnswerChange={onAnswerChange} disabled={disabled} />
      </Box>
    );
  }

  return (
    <Box>
      {question.isRequired && (
        <Typography
          sx={{
            textAlign: align,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: 0.6,
            color: '#ff4d6d',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          Required *
        </Typography>
      )}
      <Typography
        component="div"
        sx={{
          textAlign: align,
          fontSize: compact ? '0.9375rem' : '1.0625rem',
          fontWeight: 700,
          lineHeight: 1.5,
          mb: question.questionBody ? (compact ? 0.5 : 1.5) : gap,
          maxWidth: FORM_CONTENT_MAX_WIDTH,
          mx: 'auto',
          '& p': { m: 0, mb: 0.5 },
          '& ul, & ol': { pl: 3, m: 0, textAlign: 'left' },
          '& a': { color: '#ff4d6d' },
          ...formRichContentSx,
        }}
        dangerouslySetInnerHTML={{
          __html: sanitizeRichHtml(question.questionText),
        }}
      />

      {question.questionBody && (
        <Typography
          component="div"
          sx={{
            textAlign: align,
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            color: 'text.secondary',
            mb: gap,
            maxWidth: FORM_CONTENT_MAX_WIDTH,
            mx: 'auto',
            '& p': { m: 0, mb: 1 },
            '& ul, & ol': { pl: 3, m: 0, mb: 1 },
            '& a': { color: '#ff4d6d' },
            ...formRichContentSx,
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichHtml(question.questionBody),
          }}
        />
      )}

      {question.explanation && (
        <Typography
          sx={{
            textAlign: align,
            fontSize: '0.9rem',
            color: 'text.secondary',
            mb: compact ? 1 : { xs: 2, md: 3 },
            maxWidth: FORM_CONTENT_MAX_WIDTH,
            mx: 'auto',
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichHtml(question.explanation || ''),
          }}
        />
      )}

      <Box sx={{ mb: compact ? 3 : 5, maxWidth: FORM_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <QuestionRenderer question={question} answer={answer} onAnswerChange={onAnswerChange} disabled={disabled} />
      </Box>
    </Box>
  );
};
